import React, { useEffect, useMemo, useState } from 'react';
import {
  simulateWhatIf, saveWhatIfScenario, listWhatIfScenarios, deleteWhatIfScenario,
  promoteToDraftPo, listPoDrafts, deletePoDraft,
} from '../lib/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Lightning, FloppyDisk, Trash, ArrowsClockwise, TrendDown, TrendUp,
  Warning, CheckCircle, FlowArrow, ShoppingCart, Check, ArrowRight,
} from '@phosphor-icons/react';
import { toast } from 'sonner';


const DEFAULT_LEVERS = {
  demand_multiplier: 1.0,
  demand_spike_week: null,
  demand_spike_multiplier: 1.0,
  lead_time_delta_days: 0,
  supplier_delay_weeks: 0,
  stock_haircut_pct: 0,
};


function LeverSlider({ label, value, onChange, min, max, step, unit, testid, hint }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-[11px] uppercase tracking-wider opacity-70 font-mono">{label}</label>
        <span className="text-[13px] font-bold tabular-nums" data-testid={`${testid}-value`}>
          {value}{unit}
        </span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary"
        data-testid={testid}
      />
      {hint && <div className="text-[10px] opacity-50 font-mono">{hint}</div>}
    </div>
  );
}


function SummaryKpi({ label, baseline, scenario, accent = 'default', testid }) {
  const delta = scenario - baseline;
  const tones = {
    default: 'border-border',
    warn: 'border-amber-500/40',
    danger: 'border-red-500/40',
  };
  return (
    <Card className={`p-4 border-2 ${tones[accent]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-60 font-mono">{label}</div>
      <div className="flex items-end gap-3 mt-1.5">
        <div>
          <div className="text-[10px] opacity-60 font-mono">baseline</div>
          <div className="text-xl font-bold tabular-nums">{baseline}</div>
        </div>
        <div className="opacity-40">→</div>
        <div>
          <div className="text-[10px] opacity-60 font-mono">scenario</div>
          <div className="text-xl font-bold tabular-nums">{scenario}</div>
        </div>
        <div className={`ml-auto text-[13px] font-mono ${delta > 0 ? 'text-red-600' : delta < 0 ? 'text-emerald-600' : 'opacity-50'}`}>
          {delta > 0 ? '+' : ''}{delta}
        </div>
      </div>
    </Card>
  );
}


export default function WhatIfSimulator() {
  const [levers, setLevers] = useState(DEFAULT_LEVERS);
  const [horizon, setHorizon] = useState(8);
  const [limit, setLimit] = useState(50);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [scenarioName, setScenarioName] = useState('');
  const [onlyDeteriorated, setOnlyDeteriorated] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [promotedSet, setPromotedSet] = useState(new Set());  // sku_ids in-flight
  const [promotingId, setPromotingId] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const res = await simulateWhatIf({
        horizon_weeks: horizon, limit,
        levers: { ...levers, demand_spike_week: levers.demand_spike_week ?? null },
      });
      setResult(res.data);
      toast.success(`Simulation complete · ${res.data.summary.sku_count} SKUs analysed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Simulation failed');
    } finally {
      setLoading(false);
    }
  };

  const loadSaved = async () => {
    try {
      const r = await listWhatIfScenarios();
      setSavedScenarios(r.data.scenarios || []);
    } catch (e) { /* ignore */ }
  };

  const loadDrafts = async () => {
    try {
      const r = await listPoDrafts({ status: 'pending_review' });
      setDrafts(r.data.drafts || []);
      setPromotedSet(new Set((r.data.drafts || []).map(d => d.sku_id)));
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadSaved(); loadDrafts(); }, []);

  const promote = async (row) => {
    if (!row.recommendation) {
      toast.error('No recommendation available for this SKU');
      return;
    }
    setPromotingId(row.sku_id);
    try {
      await promoteToDraftPo({
        sku_id: row.sku_id,
        sku_code: row.sku_code,
        sku_name: row.sku_name,
        recommended_qty: row.recommendation.recommended_qty,
        order_by_date: row.recommendation.order_by_date,
        scenario_name: scenarioName || 'Ad-hoc What-If run',
        notes: `Scenario Δmin=${row.min_closing_delta} · urgency=${row.recommendation.urgency}`,
      });
      toast.success(`Draft PO created for ${row.sku_code}`);
      loadDrafts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Promotion failed');
    } finally {
      setPromotingId(null);
    }
  };

  const removeDraft = async (id) => {
    try {
      await deletePoDraft(id);
      toast.success('Draft removed');
      loadDrafts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const save = async () => {
    if (!scenarioName.trim()) {
      toast.error('Give the scenario a name first');
      return;
    }
    try {
      await saveWhatIfScenario({
        name: scenarioName.trim(),
        horizon_weeks: horizon,
        levers: { ...levers, demand_spike_week: levers.demand_spike_week ?? null },
      });
      toast.success('Scenario saved');
      setScenarioName('');
      loadSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const loadScenario = (s) => {
    setLevers({ ...DEFAULT_LEVERS, ...(s.levers || {}) });
    setHorizon(s.horizon_weeks || 8);
    toast.success(`Loaded "${s.name}"`);
  };

  const removeScenario = async (id) => {
    try {
      await deleteWhatIfScenario(id);
      toast.success('Deleted');
      loadSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const reset = () => {
    setLevers(DEFAULT_LEVERS);
    setResult(null);
  };

  const filteredDiff = useMemo(() => {
    if (!result?.diff) return [];
    return onlyDeteriorated ? result.diff.filter(d => d.deteriorated) : result.diff;
  }, [result, onlyDeteriorated]);

  const isDirty = JSON.stringify(levers) !== JSON.stringify(DEFAULT_LEVERS);

  return (
    <div className="space-y-5" data-testid="whatif-simulator">
      <Card className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Lightning className="size-5 text-amber-500" weight="duotone" />
              <h3 className="text-lg font-bold tracking-tight">
                Scenario Levers
              </h3>
            </div>
            <p className="text-[12px] opacity-70 mt-1 max-w-2xl">
              Stress-test your projection. Adjust levers below and hit <span className="font-bold">Run Simulation</span> —
              we'll show your current plan vs the scenario side-by-side so you can see which SKUs break under pressure.
            </p>
          </div>
          {isDirty && (
            <Button size="sm" variant="outline" onClick={reset} data-testid="whatif-reset">
              <ArrowsClockwise className="size-3.5 mr-1" /> Reset levers
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <LeverSlider
            label="Demand Multiplier"
            value={levers.demand_multiplier}
            onChange={(v) => setLevers({ ...levers, demand_multiplier: v })}
            min={0} max={3} step={0.05} unit="×"
            testid="lever-demand-multiplier"
            hint="1.0 = baseline · 1.2 = +20% · 2.0 = double demand"
          />
          <LeverSlider
            label="Stock Haircut"
            value={levers.stock_haircut_pct}
            onChange={(v) => setLevers({ ...levers, stock_haircut_pct: v })}
            min={0} max={100} step={5} unit="%"
            testid="lever-stock-haircut"
            hint="simulate lost pallet / inventory write-off"
          />
          <LeverSlider
            label="Supplier Delay"
            value={levers.supplier_delay_weeks}
            onChange={(v) => setLevers({ ...levers, supplier_delay_weeks: v })}
            min={0} max={12} step={1} unit=" wk"
            testid="lever-supplier-delay"
            hint="push all inbound POs this many weeks later"
          />
          <LeverSlider
            label="Lead-Time Delta"
            value={levers.lead_time_delta_days}
            onChange={(v) => setLevers({ ...levers, lead_time_delta_days: v })}
            min={-30} max={90} step={1} unit=" d"
            testid="lever-lead-time"
            hint="extra days added to supplier lead time"
          />
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wider opacity-70 font-mono">Spike Week</label>
            <select
              value={levers.demand_spike_week ?? ''}
              onChange={(e) => setLevers({ ...levers, demand_spike_week: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
              className="w-full h-9 px-2 text-[12px] bg-background border border-border rounded font-mono"
              data-testid="lever-spike-week"
            >
              <option value="">— none —</option>
              {Array.from({ length: horizon }).map((_, i) => (
                <option key={i} value={i}>Week {i + 1}</option>
              ))}
            </select>
            <div className="text-[10px] opacity-50 font-mono">e.g. Black Friday, promo launch</div>
          </div>
          <LeverSlider
            label="Spike Multiplier"
            value={levers.demand_spike_multiplier}
            onChange={(v) => setLevers({ ...levers, demand_spike_multiplier: v })}
            min={1} max={5} step={0.25} unit="×"
            testid="lever-spike-multiplier"
            hint={levers.demand_spike_week == null ? 'select a spike week to activate' : 'multiplier applied on spike week'}
          />
        </div>

        <div className="flex items-center gap-3 mt-5 flex-wrap">
          <label className="text-[11px] opacity-70 font-mono">Horizon:</label>
          <select value={horizon} onChange={(e) => setHorizon(parseInt(e.target.value, 10))}
                  className="h-8 px-2 text-[12px] bg-background border border-border rounded font-mono"
                  data-testid="whatif-horizon">
            {[4, 8, 12, 16, 26].map(w => <option key={w} value={w}>{w} wk</option>)}
          </select>
          <label className="text-[11px] opacity-70 font-mono ml-2">SKUs:</label>
          <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                  className="h-8 px-2 text-[12px] bg-background border border-border rounded font-mono"
                  data-testid="whatif-limit">
            {[25, 50, 100, 250].map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <div className="ml-auto flex items-center gap-2">
            <Button
              onClick={run} disabled={loading}
              data-testid="whatif-run-btn"
              className="font-bold"
            >
              <Lightning className="size-4 mr-1" weight="fill" />
              {loading ? 'Simulating…' : 'Run Simulation'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Saved scenarios */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <FlowArrow className="size-4 opacity-60" />
          <h3 className="text-[13px] font-bold uppercase tracking-wider">Saved Scenarios</h3>
        </div>
        <div className="flex gap-2 mb-3">
          <Input
            value={scenarioName}
            onChange={(e) => setScenarioName(e.target.value)}
            placeholder="e.g. Black Friday +50% + 2wk supplier delay"
            className="h-9 text-[12px]"
            data-testid="scenario-name-input"
          />
          <Button size="sm" variant="outline" onClick={save} data-testid="scenario-save-btn">
            <FloppyDisk className="size-3.5 mr-1" /> Save current
          </Button>
        </div>
        {savedScenarios.length === 0 ? (
          <div className="text-[12px] opacity-60">No scenarios saved yet. Save one above to reuse later.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {savedScenarios.map(s => (
              <div key={s.id}
                   className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-muted/40 text-[11px] font-mono hover:bg-muted transition-colors"
                   data-testid={`scenario-chip-${s.id}`}>
                <button onClick={() => loadScenario(s)} className="hover:underline">
                  {s.name}
                </button>
                <button onClick={() => removeScenario(s.id)} className="opacity-50 hover:opacity-100 hover:text-red-600">
                  <Trash className="size-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Results */}
      {result && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <SummaryKpi
              label="SKUs Analysed"
              baseline={result.summary.sku_count}
              scenario={result.summary.sku_count}
              testid="summary-sku-count"
            />
            <SummaryKpi
              label="Projected Stockouts"
              baseline={result.summary.stockouts_baseline}
              scenario={result.summary.stockouts_scenario}
              accent={result.summary.new_stockouts > 0 ? 'danger' : 'default'}
              testid="summary-stockouts"
            />
            <Card className="p-4 border-2 border-amber-500/40">
              <div className="text-[10px] uppercase tracking-wider opacity-60 font-mono">New Stockouts</div>
              <div className="text-3xl font-bold mt-1 tabular-nums" data-testid="summary-new-stockouts">
                {result.summary.new_stockouts}
              </div>
              <div className="text-[11px] opacity-70 mt-1">
                {result.summary.new_stockouts === 0
                  ? 'Plan survives this scenario'
                  : 'SKUs that go OOS only under scenario'}
              </div>
            </Card>
            <Card className={`p-4 border-2 ${result.summary.deteriorations > 0 ? 'border-red-500/40' : 'border-emerald-500/40'}`}>
              <div className="text-[10px] uppercase tracking-wider opacity-60 font-mono">Deteriorations</div>
              <div className="text-3xl font-bold mt-1 tabular-nums" data-testid="summary-deteriorations">
                {result.summary.deteriorations}
              </div>
              <div className="text-[11px] opacity-70 mt-1 flex items-center gap-1">
                {result.summary.deteriorations > 0
                  ? <><Warning className="size-3 text-red-600" /> SKUs worse off under scenario</>
                  : <><CheckCircle className="size-3 text-emerald-600" /> No SKUs deteriorate</>}
              </div>
            </Card>
          </div>

          {/* Draft POs created from What-If */}
          {drafts.length > 0 && (
            <Card className="p-4 border-2 border-blue-500/30" data-testid="whatif-drafts-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="size-4 text-blue-600" weight="duotone" />
                  <h3 className="text-[13px] font-bold uppercase tracking-wider">
                    Draft POs · Pending Review ({drafts.length})
                  </h3>
                </div>
                <div className="text-[11px] opacity-60 font-mono">
                  {drafts.reduce((s, d) => s + d.recommended_qty, 0)} units ·
                  ${drafts.reduce((s, d) => s + (d.estimated_cost || 0), 0).toFixed(2)}
                </div>
              </div>
              <div className="space-y-2">
                {drafts.slice(0, 5).map(d => (
                  <div key={d.id}
                       className="flex items-center gap-3 p-2.5 bg-muted/30 rounded border border-border"
                       data-testid={`draft-po-${d.sku_code}`}>
                    <ArrowRight className="size-3.5 text-blue-600" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[12px]">
                        <span className="font-mono font-bold">{d.sku_code}</span>
                        <span className="opacity-60 truncate">{d.sku_name}</span>
                      </div>
                      <div className="text-[10px] opacity-60 font-mono">
                        +{d.recommended_qty} units · order by {d.order_by_date} ·
                        scenario: {d.scenario_name || '—'}
                      </div>
                    </div>
                    <button
                      onClick={() => removeDraft(d.id)}
                      className="opacity-40 hover:opacity-100 hover:text-red-600"
                      data-testid={`draft-po-delete-${d.sku_code}`}
                    >
                      <Trash className="size-3.5" />
                    </button>
                  </div>
                ))}
                {drafts.length > 5 && (
                  <div className="text-[11px] opacity-60 font-mono pl-6">
                    … and {drafts.length - 5} more pending
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Side-by-side diff table */}
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b border-border">
              <div className="text-[12px] font-bold uppercase tracking-wider">
                Baseline ↔ Scenario Diff
              </div>
              <label className="flex items-center gap-2 text-[11px] opacity-70 font-mono cursor-pointer">
                <input type="checkbox" checked={onlyDeteriorated}
                       onChange={(e) => setOnlyDeteriorated(e.target.checked)}
                       data-testid="whatif-filter-deteriorated" />
                Show only deteriorations
              </label>
            </div>
            <div className="overflow-auto max-h-[60vh]">
              <table className="w-full text-[12px]" data-testid="whatif-diff-table">
                <thead className="bg-muted/40 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">SKU</th>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-right px-3 py-2">Baseline Min</th>
                    <th className="text-right px-3 py-2">Scenario Min</th>
                    <th className="text-right px-3 py-2">Δ Min</th>
                    <th className="text-center px-3 py-2">Risk</th>
                    <th className="text-left px-3 py-2">Recommended Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDiff.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 opacity-60 text-[12px]">
                        {onlyDeteriorated ? 'No deteriorations — plan holds up' : 'No diff data'}
                      </td>
                    </tr>
                  ) : filteredDiff.map(d => {
                    const rec = d.recommendation;
                    const already = promotedSet.has(d.sku_id);
                    const urgencyTone = {
                      critical: 'bg-red-500/15 text-red-600 border-red-500/30',
                      high:     'bg-amber-500/15 text-amber-600 border-amber-500/30',
                      medium:   'bg-blue-500/15 text-blue-600 border-blue-500/30',
                    };
                    return (
                      <tr key={d.sku_id}
                          className={`border-t border-border/40 ${d.deteriorated ? 'bg-red-500/5' : ''}`}
                          data-testid={`whatif-diff-row-${d.sku_code}`}>
                        <td className="px-3 py-2 font-mono font-bold">{d.sku_code}</td>
                        <td className="px-3 py-2 opacity-70 truncate max-w-[180px]">{d.sku_name}</td>
                        <td className={`px-3 py-2 text-right font-mono ${d.baseline_min <= 0 ? 'text-red-600' : ''}`}>
                          {d.baseline_min}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono font-bold ${d.scenario_min <= 0 ? 'text-red-600' : ''}`}>
                          {d.scenario_min}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono ${d.min_closing_delta < 0 ? 'text-red-600 font-bold' : d.min_closing_delta > 0 ? 'text-emerald-600' : 'opacity-50'}`}>
                          {d.min_closing_delta > 0 ? '+' : ''}{d.min_closing_delta}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {d.risk_changed ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono bg-red-500/15 text-red-600 font-bold">
                              <TrendDown className="size-3" />
                              {d.baseline_risk} → {d.scenario_risk}
                            </span>
                          ) : (
                            <span className="text-[10px] opacity-50 font-mono">{d.baseline_risk}</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {rec ? (
                            <div className="flex items-center gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider border ${urgencyTone[rec.urgency] || ''}`}>
                                    {rec.urgency}
                                  </span>
                                  <span className="font-bold font-mono" data-testid={`rec-qty-${d.sku_code}`}>
                                    +{rec.recommended_qty} units
                                  </span>
                                </div>
                                <div className="text-[10px] opacity-70 mt-0.5 font-mono">
                                  Order by <span className="font-bold">{rec.order_by_date}</span> ·
                                  lead {rec.lead_time_days}d · MOQ {rec.moq}
                                </div>
                              </div>
                              {already ? (
                                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-mono font-bold whitespace-nowrap"
                                      data-testid={`rec-already-promoted-${d.sku_code}`}>
                                  <Check className="size-3" /> Drafted
                                </span>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="default"
                                  className="h-7 text-[11px] whitespace-nowrap"
                                  disabled={promotingId === d.sku_id}
                                  onClick={() => promote(d)}
                                  data-testid={`rec-promote-btn-${d.sku_code}`}
                                >
                                  <ShoppingCart className="size-3 mr-1" />
                                  {promotingId === d.sku_id ? '…' : 'Draft PO'}
                                </Button>
                              )}
                            </div>
                          ) : (
                            <span className="text-[10px] opacity-40 font-mono">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
